import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  importStockData,
  importCryptoData,
  importForexData,
  importSchoolData,
  ForexImportStarted,
  importFundamentals,
  importNewsData,
  importEconomicData,
  importEarningsCalendar,
  uploadDataset,
  listImportHistory,
  FundamentalsImportParams,
  FundamentalsImportResult,
  NewsImportParams,
  NewsImportResult,
  EconomicImportParams,
  EconomicImportResult,
  CalendarImportParams,
  CalendarImportResult,
  ImportResult,
  ImportHistoryItem,
  ApiError,
} from '../api/client';

type Interval = '1min' | '5min' | '15min' | '30min' | '60min' | 'daily';
type DatasetType = 'fundamentals' | 'news' | 'calendar';

const INTERVALS: { value: Interval; label: string }[] = [
  { value: '1min', label: '1 Minute' },
  { value: '5min', label: '5 Minutes' },
  { value: '15min', label: '15 Minutes' },
  { value: '30min', label: '30 Minutes' },
  { value: '60min', label: '60 Minutes' },
  { value: 'daily', label: 'Daily' },
];

const DATASET_TYPES: { value: DatasetType; label: string }[] = [
  { value: 'fundamentals', label: 'Fundamentals' },
  { value: 'news', label: 'News & Sentiment' },
  { value: 'calendar', label: 'Calendar Events' },
];

const ECONOMIC_INDICATORS: { value: EconomicImportParams['indicator']; label: string }[] = [
  { value: 'real_gdp', label: 'Real GDP' },
  { value: 'treasury_yield', label: 'Treasury Yield (10-Year)' },
  { value: 'federal_funds_rate', label: 'Federal Funds Rate' },
  { value: 'cpi', label: 'CPI' },
  { value: 'inflation', label: 'Inflation (5-Year Breakeven)' },
  { value: 'unemployment', label: 'Unemployment Rate' },
];

export function DataImportPage() {
  return (
    <div className="page">
      <h2>Data Import</h2>
      <p style={{ marginBottom: '24px' }}>
        Import historical market data from external sources or upload datasets manually.
      </p>

      <div className="import-sections">
        <StockImportSection />
        <CryptoImportSection />
        <ForexImportSection />
        <SchoolDataSection />
        <FundamentalsImportSection />
        <NewsImportSection />
        <EconomicImportSection />
        <CalendarImportSection />
        <DatasetUploadSection />
        <ImportHistorySection />
      </div>
    </div>
  );
}

function StockImportSection() {
  const queryClient = useQueryClient();
  const [symbol, setSymbol] = useState('');
  const [interval, setInterval] = useState<Interval>('daily');
  const [dateMode, setDateMode] = useState<'range' | 'lookback'>('range');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lookbackDays, setLookbackDays] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importStockData,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Stock import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol) {
      setError('Symbol is required');
      return;
    }
    if (dateMode === 'range') {
      if (!startDate || !endDate) {
        setError('Start date and end date are required');
        return;
      }
      mutation.mutate({ symbol, startDate, endDate, interval });
    } else {
      const days = parseInt(lookbackDays, 10);
      if (!lookbackDays || Number.isNaN(days) || days < 1) {
        setError('Enter a valid number of days to look back');
        return;
      }
      mutation.mutate({ symbol, endDate: endDate || undefined, lookbackDays: days, interval });
    }
  };

  return (
    <section className="import-section">
      <h3>Import Stock Data</h3>
      <p className="section-description">Import OHLCV data from Yahoo Finance for US stocks.</p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="stock-symbol">Symbol</label>
            <input
              id="stock-symbol"
              type="text"
              className="input-field"
              placeholder="e.g., AAPL"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            />
          </div>
          <div className="form-field">
            <label htmlFor="stock-interval">Interval</label>
            <select
              id="stock-interval"
              className="input-field"
              value={interval}
              onChange={(e) => setInterval(e.target.value as Interval)}
            >
              {INTERVALS.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Date Range Mode</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="stock-datemode"
                  checked={dateMode === 'range'}
                  onChange={() => setDateMode('range')}
                /> Explicit range
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="stock-datemode"
                  checked={dateMode === 'lookback'}
                  onChange={() => setDateMode('lookback')}
                /> Look-back days
              </label>
            </div>
          </div>
        </div>

        {dateMode === 'range' ? (
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="stock-start">Start Date</label>
              <input
                id="stock-start"
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="stock-end">End Date</label>
              <input
                id="stock-end"
                type="date"
                className="input-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="stock-lookback">Days to Look Back</label>
              <input
                id="stock-lookback"
                type="number"
                min="1"
                className="input-field"
                placeholder="e.g., 90"
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="stock-end-lb">End Date (optional)</label>
              <input
                id="stock-end-lb"
                type="date"
                className="input-field"
                placeholder="Today if empty"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing...' : 'Import Stock Data'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && <ImportResultDisplay result={result} />}
    </section>
  );
}

function CryptoImportSection() {
  const queryClient = useQueryClient();
  const [symbol, setSymbol] = useState('');
  const [interval, setInterval] = useState<Interval>('daily');
  const [dateMode, setDateMode] = useState<'range' | 'lookback'>('range');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lookbackDays, setLookbackDays] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importCryptoData,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Crypto import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol) {
      setError('Symbol is required');
      return;
    }
    if (dateMode === 'range') {
      if (!startDate || !endDate) {
        setError('Start date and end date are required');
        return;
      }
      mutation.mutate({ symbol, startDate, endDate, interval });
    } else {
      const days = parseInt(lookbackDays, 10);
      if (!lookbackDays || Number.isNaN(days) || days < 1) {
        setError('Enter a valid number of days to look back');
        return;
      }
      mutation.mutate({ symbol, endDate: endDate || undefined, lookbackDays: days, interval });
    }
  };

  return (
    <section className="import-section">
      <h3>Import Crypto Data</h3>
      <p className="section-description">Import kline data from Binance for cryptocurrency pairs.</p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="crypto-symbol">Symbol</label>
            <input
              id="crypto-symbol"
              type="text"
              className="input-field"
              placeholder="e.g., BTCUSDT"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            />
          </div>
          <div className="form-field">
            <label htmlFor="crypto-interval">Interval</label>
            <select
              id="crypto-interval"
              className="input-field"
              value={interval}
              onChange={(e) => setInterval(e.target.value as Interval)}
            >
              {INTERVALS.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Date Range Mode</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="crypto-datemode"
                  checked={dateMode === 'range'}
                  onChange={() => setDateMode('range')}
                /> Explicit range
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="crypto-datemode"
                  checked={dateMode === 'lookback'}
                  onChange={() => setDateMode('lookback')}
                /> Look-back days
              </label>
            </div>
          </div>
        </div>

        {dateMode === 'range' ? (
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="crypto-start">Start Date</label>
              <input
                id="crypto-start"
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="crypto-end">End Date</label>
              <input
                id="crypto-end"
                type="date"
                className="input-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="crypto-lookback">Days to Look Back</label>
              <input
                id="crypto-lookback"
                type="number"
                min="1"
                className="input-field"
                placeholder="e.g., 90"
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="crypto-end-lb">End Date (optional)</label>
              <input
                id="crypto-end-lb"
                type="date"
                className="input-field"
                placeholder="Today if empty"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing...' : 'Import Crypto Data'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && <ImportResultDisplay result={result} />}
    </section>
  );
}

function SchoolDataSection() {
  const queryClient = useQueryClient();
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: importSchoolData,
    onSuccess: () => {
      setStarted(true);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Could not start the import');
    },
  });

  return (
    <section className="import-section">
      <h3>Load Trading School Data</h3>
      <p className="section-description">
        Loads everything the trading school's lessons use, in one go: daily prices for the platform's 10 stocks,
        12 US index funds (SPY, QQQ and sector funds) and 5 cryptocurrencies from Yahoo Finance, plus the FRED
        economic series (interest rates, the VIX, the dollar index). Runs in the background for a few minutes;
        progress shows as SCHOOL-DATA in the import history. Safe to run again: it only adds what is missing.
        Currencies are loaded separately, with Import Forex Data above.
      </p>
      <button type="button" className="btn btn--primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? 'Starting...' : 'Load Trading School Data'}
      </button>
      {error && <div className="error-message">{error}</div>}
      {started && <div className="success-message">Started. Watch SCHOOL-DATA in the import history below.</div>}
    </section>
  );
}

const FOREX_PAIRS = ['EURUSD', 'GBPUSD', 'AUDUSD', 'XAUUSD'];

function ForexImportSection() {
  const queryClient = useQueryClient();
  const [symbol, setSymbol] = useState(FOREX_PAIRS[0]!);
  const [startDate, setStartDate] = useState('2010-01-01');
  const [started, setStarted] = useState<ForexImportStarted | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importForexData,
    onSuccess: (data) => {
      setStarted(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Forex import failed');
      setStarted(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate) {
      setError('Start date is required');
      return;
    }
    mutation.mutate({ symbol, startDate });
  };

  return (
    <section className="import-section">
      <h3>Import Forex Data</h3>
      <p className="section-description">
        Import hourly and daily bars from Dukascopy for a currency pair or gold. The import runs in the
        background and is slow (Dukascopy limits downloads), so a long history takes hours; watch the
        import history below. Running it again resumes where it stopped.
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="forex-symbol">Pair</label>
            <select
              id="forex-symbol"
              className="input-field"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
            >
              {FOREX_PAIRS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="forex-start">Start Date</label>
            <input
              id="forex-start"
              type="date"
              className="input-field"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
        </div>

        <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Starting...' : 'Import Forex Data'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {started && (
        <div className="success-message">
          Started importing {started.symbol} from {started.dateRange.start} to {started.dateRange.end}.
        </div>
      )}
    </section>
  );
}

function FundamentalsImportSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FundamentalsImportParams>({
    symbol: '',
    apiKey: '',
  });
  const [result, setResult] = useState<FundamentalsImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importFundamentals,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Fundamentals import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.symbol || !form.apiKey) {
      setError('Symbol and API key are required');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <section className="import-section">
      <h3>Import Fundamentals</h3>
      <p className="section-description">
        Import company overview, financial statements, and earnings from Alpha Vantage.
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="fund-symbol">Symbol</label>
            <input
              id="fund-symbol"
              type="text"
              className="input-field"
              placeholder="e.g., AAPL"
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="fund-apikey">Alpha Vantage API Key</label>
            <input
              id="fund-apikey"
              type="text"
              className="input-field"
              placeholder="Your API key"
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing (~60s)...' : 'Import Fundamentals'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && (
        <div className="import-result">
          <h4>Import Successful</h4>
          <dl className="result-details">
            <dt>Symbol</dt>
            <dd>{result.symbol}</dd>
            <dt>Overview</dt>
            <dd>{result.overviewImported ? 'Imported' : 'Skipped'}</dd>
            <dt>Income Statements</dt>
            <dd>{result.incomeStatements}</dd>
            <dt>Balance Sheets</dt>
            <dd>{result.balanceSheets}</dd>
            <dt>Cash Flow Statements</dt>
            <dd>{result.cashFlowStatements}</dd>
            <dt>Earnings Reports</dt>
            <dd>{result.earningsReports}</dd>
          </dl>
        </div>
      )}
    </section>
  );
}

function NewsImportSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<NewsImportParams>({
    symbol: '',
    startDate: '',
    endDate: '',
    apiKey: '',
  });
  const [result, setResult] = useState<NewsImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importNewsData,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'News import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.symbol || !form.startDate || !form.endDate || !form.apiKey) {
      setError('All fields are required');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <section className="import-section">
      <h3>Import News</h3>
      <p className="section-description">
        Import news articles and sentiment data from Alpha Vantage.
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="news-symbol">Symbol</label>
            <input
              id="news-symbol"
              type="text"
              className="input-field"
              placeholder="e.g., AAPL"
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="news-apikey">Alpha Vantage API Key</label>
            <input
              id="news-apikey"
              type="text"
              className="input-field"
              placeholder="Your API key"
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="news-start">Start Date</label>
            <input
              id="news-start"
              type="date"
              className="input-field"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="news-end">End Date</label>
            <input
              id="news-end"
              type="date"
              className="input-field"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing...' : 'Import News'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && (
        <div className="import-result">
          <h4>Import Successful</h4>
          <dl className="result-details">
            <dt>Symbol</dt>
            <dd>{result.symbol}</dd>
            <dt>Articles Imported</dt>
            <dd>{result.articlesImported}</dd>
            <dt>Duplicates Skipped</dt>
            <dd>{result.duplicatesSkipped}</dd>
          </dl>
        </div>
      )}
    </section>
  );
}

function EconomicImportSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<EconomicImportParams>({
    indicator: 'cpi',
    startDate: '',
    endDate: '',
    fredApiKey: '',
  });
  const [result, setResult] = useState<EconomicImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importEconomicData,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Economic data import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.startDate || !form.endDate || !form.fredApiKey) {
      setError('All fields are required');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <section className="import-section">
      <h3>Import Economic Data</h3>
      <p className="section-description">
        Import macroeconomic indicators from FRED (Federal Reserve Economic Data).
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="econ-indicator">Indicator</label>
            <select
              id="econ-indicator"
              className="input-field"
              value={form.indicator}
              onChange={(e) => setForm({ ...form, indicator: e.target.value as EconomicImportParams['indicator'] })}
            >
              {ECONOMIC_INDICATORS.map((ind) => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="econ-apikey">FRED API Key</label>
            <input
              id="econ-apikey"
              type="text"
              className="input-field"
              placeholder="Your FRED API key"
              value={form.fredApiKey}
              onChange={(e) => setForm({ ...form, fredApiKey: e.target.value })}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="econ-start">Start Date</label>
            <input
              id="econ-start"
              type="date"
              className="input-field"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="econ-end">End Date</label>
            <input
              id="econ-end"
              type="date"
              className="input-field"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing...' : 'Import Economic Data'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && (
        <div className="import-result">
          <h4>Import Successful</h4>
          <dl className="result-details">
            <dt>Indicator</dt>
            <dd>{result.indicator}</dd>
            <dt>Data Points Imported</dt>
            <dd>{result.dataPointsImported}</dd>
            <dt>Duplicates Skipped</dt>
            <dd>{result.duplicatesSkipped}</dd>
          </dl>
        </div>
      )}
    </section>
  );
}

function CalendarImportSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CalendarImportParams>({
    symbol: '',
    apiKey: '',
  });
  const [result, setResult] = useState<CalendarImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: importEarningsCalendar,
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Calendar import failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.apiKey) {
      setError('API key is required');
      return;
    }
    mutation.mutate({
      apiKey: form.apiKey,
      symbol: form.symbol || undefined,
    });
  };

  return (
    <section className="import-section">
      <h3>Import Earnings Calendar</h3>
      <p className="section-description">
        Import upcoming earnings calendar from Alpha Vantage. Symbol is optional.
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="cal-symbol">Symbol (optional)</label>
            <input
              id="cal-symbol"
              type="text"
              className="input-field"
              placeholder="e.g., AAPL (leave empty for all)"
              value={form.symbol ?? ''}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="cal-apikey">Alpha Vantage API Key</label>
            <input
              id="cal-apikey"
              type="text"
              className="input-field"
              placeholder="Your API key"
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Importing...' : 'Import Earnings Calendar'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && (
        <div className="import-result">
          <h4>Import Successful</h4>
          <dl className="result-details">
            <dt>Events Imported</dt>
            <dd>{result.eventsImported}</dd>
            <dt>Duplicates Skipped</dt>
            <dd>{result.duplicatesSkipped}</dd>
          </dl>
        </div>
      )}
    </section>
  );
}

function DatasetUploadSection() {
  const queryClient = useQueryClient();
  const [datasetType, setDatasetType] = useState<DatasetType>('fundamentals');
  const [jsonText, setJsonText] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ type, records }: { type: DatasetType; records: unknown[] }) =>
      uploadDataset(type, records),
    onSuccess: (data) => {
      setResult(data.data);
      setError(null);
      setJsonText('');
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Dataset upload failed');
      setResult(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!jsonText.trim()) {
      setError('JSON records are required');
      return;
    }

    let records: unknown[];
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setError('JSON must be an array of records');
        return;
      }
      records = parsed;
    } catch {
      setError('Invalid JSON format. Please provide a valid JSON array.');
      return;
    }

    if (records.length === 0) {
      setError('At least one record is required');
      return;
    }

    mutation.mutate({ type: datasetType, records });
  };

  return (
    <section className="import-section">
      <h3>Upload Dataset</h3>
      <p className="section-description">
        Upload JSON datasets for fundamentals, news/sentiment, or calendar events.
      </p>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="dataset-type">Dataset Type</label>
            <select
              id="dataset-type"
              className="input-field"
              value={datasetType}
              onChange={(e) => setDatasetType(e.target.value as DatasetType)}
            >
              {DATASET_TYPES.map((dt) => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-field">
          <label htmlFor="dataset-json">JSON Records</label>
          <textarea
            id="dataset-json"
            className="input-field textarea-field"
            placeholder='[{"symbol": "AAPL", "effective_date": "2023-01-15", ...}]'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            rows={8}
          />
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Uploading...' : 'Upload Dataset'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}
      {result && <ImportResultDisplay result={result} />}
    </section>
  );
}

function ImportHistorySection() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['importHistory'],
    queryFn: listImportHistory,
    refetchInterval: 10_000,
  });

  return (
    <section className="import-section">
      <h3>Import History</h3>
      <p className="section-description">Recent imports and their status.</p>

      {isLoading && <p>Loading import history...</p>}
      {isError && <div className="error-message">Failed to load import history</div>}

      {data && data.data.length === 0 && (
        <p>No imports have been performed yet.</p>
      )}

      {data && data.data.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Interval</th>
              <th>Status</th>
              <th>Records</th>
              <th>Duplicates</th>
              <th>Completed</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((item: ImportHistoryItem) => (
              <tr key={item.importId}>
                <td><strong>{item.symbol}</strong></td>
                <td>{item.interval}</td>
                <td>
                  <span className={`badge badge--${item.status}`}>
                    {item.status}
                  </span>
                </td>
                <td>{item.recordsImported}</td>
                <td>{item.duplicatesSkipped}</td>
                <td>{item.completedAt ? new Date(item.completedAt).toLocaleString() : '-'}</td>
                <td className="error-text">{item.error ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ImportResultDisplay({ result }: { result: ImportResult }) {
  return (
    <div className="import-result">
      <h4>Import Successful</h4>
      <dl className="result-details">
        <dt>Symbol</dt>
        <dd>{result.symbol}</dd>
        <dt>Interval</dt>
        <dd>{result.interval}</dd>
        <dt>Records Imported</dt>
        <dd>{result.recordsImported}</dd>
        <dt>Duplicates Skipped</dt>
        <dd>{result.duplicatesSkipped}</dd>
        <dt>Date Range</dt>
        <dd>{result.dateRange.start} &mdash; {result.dateRange.end}</dd>
      </dl>
    </div>
  );
}
